import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import apiService from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../contexts/ToastContext';

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#32325d',
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4'
      }
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a'
    }
  }
};

const StripePaymentForm = ({ onSuccess, buttonText, processingText }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { addToast } = useToast();
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    // Ensure the user has a Stripe customer ID
    const ensureCustomer = async () => {
      if (!user.stripeCustomerId) {
        try {
          const response = await apiService.createStripeCustomer();
          // Customer created successfully, no need to do anything else
          console.log('Stripe customer created:', response);
        } catch (err) {
          console.error('Error creating Stripe customer:', err);
          setError(t('payment.errors.customerCreation'));
        }
      }
    };

    if (user) {
      ensureCustomer();
    }
  }, [user, t]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    if (error) {
      elements.getElement('card').focus();
      return;
    }

    if (!cardComplete) {
      return;
    }

    setProcessing(true);

    try {
      const cardElement = elements.getElement(CardElement);
      
      // Create payment method
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (error) {
        setError(error.message);
        setProcessing(false);
        return;
      }

      // Add payment method to customer
      const response = await apiService.addPaymentMethod(paymentMethod.id);
      
      addToast({
        type: 'success',
        message: t('payment.success.paymentMethodAdded')
      });
      
      if (onSuccess) {
        onSuccess(response);
      }
      
      // Reset form
      cardElement.clear();
      setCardComplete(false);
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message || t('payment.errors.generic'));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-4 border border-gray-300 rounded-md">
        <CardElement
          options={CARD_ELEMENT_OPTIONS}
          onChange={(e) => {
            setError(e.error ? e.error.message : '');
            setCardComplete(e.complete);
          }}
        />
      </div>
      
      {error && (
        <div className="text-red-500 text-sm mt-2">
          {error}
        </div>
      )}
      
      <button
        type="submit"
        disabled={!stripe || processing || !cardComplete}
        className={`w-full py-2 px-4 rounded-md ${
          !stripe || processing || !cardComplete
            ? 'bg-gray-300 cursor-not-allowed'
            : 'bg-blue-600 hover:bg-blue-700 text-white'
        } transition-colors duration-200`}
      >
        {processing ? processingText || t('payment.processing') : buttonText || t('payment.addPaymentMethod')}
      </button>
    </form>
  );
};

export default StripePaymentForm;
